// =====================================================
// WORD INDEX: which word of its side a diff piece is. Every piece the comparison makes carries it
// (DiffEntry.origWordIdx for a deleted, moved-from or unchanged piece; revWordIdx for an inserted,
// moved-to or unchanged one), and every writer reads it through wordOf, which fails loudly when it
// is missing instead of taking the paragraph's first word (stage 6 of the engine cleanup).
// =====================================================

import type { DiffEntry } from '../types';

/** A diff piece's word on one side: its index in that side's words (`origWordIdx` / `revWordIdx`).
 *  Every piece has its side's; one without is a bug in the pass that made it, so this fails
 *  loudly (until stage 6 of the engine cleanup the writers took word 0, formatting the piece like
 *  the paragraph's first word). */
export function wordOf(d: DiffEntry, side: 'orig' | 'rev'): number {
  const w = side === 'orig' ? d.origWordIdx : d.revWordIdx;
  if (w == null) throw new Error(`Fredline: a diff piece (${d.type} "${d.value}") has no ${side === 'orig' ? 'original' : 'revised'} word`);
  return w;
}

/** A word diff's entries (diffArrays) with each piece's word on its side: `origWordIdx` from
 *  `idx` for a deleted word or `aIdx` for an unchanged one, `revWordIdx` from `idx` for an
 *  inserted word or `bIdx` for an unchanged one. */
export function withWordIndices(entries: DiffEntry[]): DiffEntry[] {
  const need = (v: number | undefined, d: DiffEntry) => {
    if (v == null) throw new Error(`Fredline: the word diff gave a piece (${d.type} "${d.value}") no position`);
    return v;
  };
  for (const d of entries) {
    if (d.type === 'delete') d.origWordIdx = need(d.idx, d);
    else if (d.type === 'insert') d.revWordIdx = need(d.idx, d);
    else if (d.type === 'equal') { d.origWordIdx = need(d.aIdx, d); d.revWordIdx = need(d.bIdx, d); }
  }
  return entries;
}
